import { Box, FormControlLabel, InputLabel, Stack, Switch, Typography, useColorScheme, useTheme } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import HelpPopover from '../../../HelpPopover';
import { updateParameter } from '../DeviceCardBuilder';
import { DeviceCardProps } from '../DeviceCardProps';
import { useControlInitialization } from '../../../../hooks/controls/useControlInitialization';
import UnsupportedCodeStatus from './UnsupportedCodeStatus';
import { isUnsupportedEnumRawValue, parseKnownEnumRawValue } from './unsupportedEnumValue';

import swerve1LeftDark from '../assets/drive-styles/Swerve1/Drive_Swerve1_Left_Dark.svg';
import swerve1LeftLight from '../assets/drive-styles/Swerve1/Drive_Swerve1_Left_Light.svg';
import swerve1RightDark from '../assets/drive-styles/Swerve1/Drive_Swerve1_Right_Dark.svg';
import swerve1RightLight from '../assets/drive-styles/Swerve1/Drive_Swerve1_Right_Light.svg';
import swerve2LeftDark from '../assets/drive-styles/Swerve2/Drive_Swerve2_Left_Dark.svg';
import swerve2LeftLight from '../assets/drive-styles/Swerve2/Drive_Swerve2_Left_Light.svg';
import swerve2RightDark from '../assets/drive-styles/Swerve2/Drive_Swerve2_Right_Dark.svg';
import swerve2RightLight from '../assets/drive-styles/Swerve2/Drive_Swerve2_Right_Light.svg';
import skid1LeftDark from '../assets/drive-styles/Skidsteer1/Drive_Skidsteer1_Left_Dark.svg';
import skid1LeftLight from '../assets/drive-styles/Skidsteer1/Drive_Skidsteer1_Left_Light.svg';
import skid1RightDark from '../assets/drive-styles/Skidsteer1/Drive_Skidsteer1_Right_Dark.svg';
import skid1RightLight from '../assets/drive-styles/Skidsteer1/Drive_Skidsteer1_Right_Light.svg';
import skid2LeftDark from '../assets/drive-styles/Skidsteer2/Drive_Skidsteer2_Left_Dark.svg';
import skid2LeftLight from '../assets/drive-styles/Skidsteer2/Drive_Skidsteer2_Left_Light.svg';
import skid2RightDark from '../assets/drive-styles/Skidsteer2/Drive_Skidsteer2_Right_Dark.svg';
import skid2RightLight from '../assets/drive-styles/Skidsteer2/Drive_Skidsteer2_Right_Light.svg';
import skid3LeftDark from '../assets/drive-styles/Skidsteer3/Drive_Skidsteer3_Left_Dark.svg';
import skid3LeftLight from '../assets/drive-styles/Skidsteer3/Drive_Skidsteer3_Left_Light.svg';
import skid4RightDark from '../assets/drive-styles/Skidsteer4/Drive_Skidsteer4_Right_Dark.svg';
import skid4RightLight from '../assets/drive-styles/Skidsteer4/Drive_Skidsteer4_Right_Light.svg';

interface ControlStickDriveStyleControlProps extends DeviceCardProps {
  labelId: string;
  label: React.ReactNode;
  labelPopover?: React.ReactNode;
  labelPopoverId?: string;
}

enum DriveType {
  SWERVE = 'DriveType.SWERVE',
  SKID_STEER = 'DriveType.SKID_STEER',
}

const DRIVE_TYPE_VALUES = [DriveType.SWERVE, DriveType.SKID_STEER] as const;

enum ControlStickDriveStyle {
  SWERVE_TWO_STICK = 'ControlStickDriveStyle.SWERVE_TWO_STICK',
  SWERVE_TWO_STICK_INVERTED = 'ControlStickDriveStyle.SWERVE_TWO_STICK_INVERTED',
  SKIDSTEER_SINGLE_STICK = 'ControlStickDriveStyle.SKIDSTEER_SINGLE_STICK',
  SKIDSTEER_SINGLE_STICK_INVERTED = 'ControlStickDriveStyle.SKIDSTEER_SINGLE_STICK_INVERTED',
  SKIDSTEER_TWO_STICK = 'ControlStickDriveStyle.SKIDSTEER_TWO_STICK',
  SKIDSTEER_TWO_STICK_INVERTED = 'ControlStickDriveStyle.SKIDSTEER_TWO_STICK_INVERTED',
}

const CONTROL_STICK_DRIVE_STYLE_VALUES = [
  ControlStickDriveStyle.SWERVE_TWO_STICK,
  ControlStickDriveStyle.SWERVE_TWO_STICK_INVERTED,
  ControlStickDriveStyle.SKIDSTEER_SINGLE_STICK,
  ControlStickDriveStyle.SKIDSTEER_SINGLE_STICK_INVERTED,
  ControlStickDriveStyle.SKIDSTEER_TWO_STICK,
  ControlStickDriveStyle.SKIDSTEER_TWO_STICK_INVERTED,
] as const;

const CONTROL_STICK_DRIVE_STYLE_ALIASES: Record<string, ControlStickDriveStyle> = {
  'ControlStickDriveStyle.SKIDSTEER_ONE_STICK': ControlStickDriveStyle.SKIDSTEER_SINGLE_STICK,
  'ControlStickDriveStyle.SKIDSTEER_ONE_STICK_INVERTED': ControlStickDriveStyle.SKIDSTEER_SINGLE_STICK_INVERTED,
};

enum ControlStickStyleOption {
  SWERVE = 'SWERVE',
  SKIDSTEER_TWO_STICK = 'SKIDSTEER_TWO_STICK',
  SKIDSTEER_ONE_STICK = 'SKIDSTEER_ONE_STICK',
}

type StickPreview = { left?: string; right?: string };

const parseDriveType = (v: unknown): DriveType => parseKnownEnumRawValue(v, DRIVE_TYPE_VALUES, DriveType.SWERVE);

const parseControlStickDriveStyle = (v: unknown): ControlStickDriveStyle => {
  const rawText = String(v ?? '').trim();
  return (
    CONTROL_STICK_DRIVE_STYLE_ALIASES[rawText] ??
    parseKnownEnumRawValue(rawText, CONTROL_STICK_DRIVE_STYLE_VALUES, ControlStickDriveStyle.SWERVE_TWO_STICK)
  );
};

const isUnsupportedControlStickDriveStyleRawValue = (v: unknown): boolean => {
  const rawText = String(v ?? '').trim();
  if (!rawText) return false;
  if (CONTROL_STICK_DRIVE_STYLE_ALIASES[rawText]) return false;
  return isUnsupportedEnumRawValue(rawText, CONTROL_STICK_DRIVE_STYLE_VALUES);
};

const getStyleOption = (style: ControlStickDriveStyle): ControlStickStyleOption => {
  if (style === ControlStickDriveStyle.SKIDSTEER_SINGLE_STICK || style === ControlStickDriveStyle.SKIDSTEER_SINGLE_STICK_INVERTED) {
    return ControlStickStyleOption.SKIDSTEER_ONE_STICK;
  }
  if (style === ControlStickDriveStyle.SKIDSTEER_TWO_STICK || style === ControlStickDriveStyle.SKIDSTEER_TWO_STICK_INVERTED) {
    return ControlStickStyleOption.SKIDSTEER_TWO_STICK;
  }
  return ControlStickStyleOption.SWERVE;
};

const isSwapped = (style: ControlStickDriveStyle): boolean =>
  style === ControlStickDriveStyle.SWERVE_TWO_STICK_INVERTED ||
  style === ControlStickDriveStyle.SKIDSTEER_SINGLE_STICK_INVERTED ||
  style === ControlStickDriveStyle.SKIDSTEER_TWO_STICK_INVERTED;

const toControlStickDriveStyle = (option: ControlStickStyleOption, swapped: boolean): ControlStickDriveStyle => {
  if (option === ControlStickStyleOption.SWERVE) {
    return swapped ? ControlStickDriveStyle.SWERVE_TWO_STICK_INVERTED : ControlStickDriveStyle.SWERVE_TWO_STICK;
  }
  if (option === ControlStickStyleOption.SKIDSTEER_ONE_STICK) {
    return swapped ? ControlStickDriveStyle.SKIDSTEER_SINGLE_STICK_INVERTED : ControlStickDriveStyle.SKIDSTEER_SINGLE_STICK;
  }
  return swapped ? ControlStickDriveStyle.SKIDSTEER_TWO_STICK_INVERTED : ControlStickDriveStyle.SKIDSTEER_TWO_STICK;
};

const normalizeOptionForDriveType = (option: ControlStickStyleOption, isSkidSteer: boolean): ControlStickStyleOption => {
  if (isSkidSteer && option === ControlStickStyleOption.SWERVE) {
    return ControlStickStyleOption.SKIDSTEER_TWO_STICK;
  }
  if (!isSkidSteer && (option === ControlStickStyleOption.SKIDSTEER_ONE_STICK || option === ControlStickStyleOption.SKIDSTEER_TWO_STICK)) {
    return ControlStickStyleOption.SWERVE;
  }
  return option;
};

const getPreview = (option: ControlStickStyleOption, swapped: boolean, isDarkTheme: boolean): StickPreview => {
  if (option === ControlStickStyleOption.SWERVE) {
    return swapped
      ? { left: isDarkTheme ? swerve2LeftDark : swerve2LeftLight, right: isDarkTheme ? swerve2RightDark : swerve2RightLight }
      : { left: isDarkTheme ? swerve1LeftDark : swerve1LeftLight, right: isDarkTheme ? swerve1RightDark : swerve1RightLight };
  }
  if (option === ControlStickStyleOption.SKIDSTEER_TWO_STICK) {
    return swapped
      ? { left: isDarkTheme ? skid2LeftDark : skid2LeftLight, right: isDarkTheme ? skid2RightDark : skid2RightLight }
      : { left: isDarkTheme ? skid1LeftDark : skid1LeftLight, right: isDarkTheme ? skid1RightDark : skid1RightLight };
  }
  return swapped ? { right: isDarkTheme ? skid4RightDark : skid4RightLight } : { left: isDarkTheme ? skid3LeftDark : skid3LeftLight };
};

const PreviewPane = ({ title, src, alt }: { title: React.ReactNode; src?: string; alt: string }) => (
  <Stack spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
    <Typography variant="body2">{title}</Typography>
    {src ? (
      <Box component="img" src={src} alt={alt} sx={{ width: '100%', maxWidth: 180, maxHeight: 130, objectFit: 'contain' }} />
    ) : (
      <Box
        sx={{
          width: '100%',
          maxWidth: 180,
          minHeight: 96,
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          <Trans i18nKey="controlStickDriveStyleControl.not_used">Not used</Trans>
        </Typography>
      </Box>
    )}
  </Stack>
);

const ControlStickDriveStyleControl = (props: ControlStickDriveStyleControlProps) => {
  const theme = useTheme();
  const { mode, systemMode } = useColorScheme();
  const { t } = useTranslation();
  const colorMode = mode === 'system' ? systemMode : mode;
  const isDark = (colorMode ?? theme.palette.mode) === 'dark';

  const { value: driveType } = useControlInitialization<DriveType>(
    { ...props, parameterName: 'drive_type' },
    {
      parseValue: parseDriveType,
    }
  );
  const {
    value: currentStyle,
    setValue: setCurrentStyle,
    unsupportedRawValue,
  } = useControlInitialization<ControlStickDriveStyle>(props, {
    parseValue: parseControlStickDriveStyle,
    isUnsupportedRawValue: isUnsupportedControlStickDriveStyleRawValue,
  });

  const isSkidSteer = driveType === DriveType.SKID_STEER;
  const selectedOption = getStyleOption(currentStyle);
  const selectedValue = normalizeOptionForDriveType(selectedOption, isSkidSteer);
  const swapped = isSwapped(currentStyle);
  const singleStick = selectedValue === ControlStickStyleOption.SKIDSTEER_ONE_STICK;
  const preview = getPreview(selectedValue, swapped, isDark);
  const isUnsupported = Boolean(unsupportedRawValue);

  const writeStyle = (option: ControlStickStyleOption, nextSwapped: boolean) => {
    const nextStyle = toControlStickDriveStyle(option, nextSwapped);
    setCurrentStyle(nextStyle);
    updateParameter(props, nextStyle);
  };

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <InputLabel id={props.labelId} shrink sx={{ position: 'static' }}>
          {props.labelPopover && props.labelPopoverId ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography>{props.label}</Typography>
              <HelpPopover id={props.labelPopoverId} text={props.labelPopover} />
            </Stack>
          ) : (
            props.label
          )}
        </InputLabel>
        <UnsupportedCodeStatus
          code={unsupportedRawValue}
          descriptionI18nKey="controlPanel.devices.unsupportedEnumParameter.description"
          descriptionDefaultValue="Unsupported setting value: {{code}}"
        />
        {isUnsupported ? (
          <Typography variant="caption" color="text.secondary">
            {t('controlStickDriveStyleControl.replacement_caption', { defaultValue: 'Choose a standard replacement style' })}
          </Typography>
        ) : null}
        <Stack spacing={0.5}>
          <FormControlLabel
            control={<Switch checked={swapped} onChange={(event) => writeStyle(selectedValue, event.target.checked)} />}
            label={<Trans i18nKey="controlStickDriveStyleControl.swap_controls">Swap controls</Trans>}
          />
          {isSkidSteer && (
            <FormControlLabel
              control={
                <Switch
                  checked={singleStick}
                  onChange={(event) =>
                    writeStyle(
                      event.target.checked ? ControlStickStyleOption.SKIDSTEER_ONE_STICK : ControlStickStyleOption.SKIDSTEER_TWO_STICK,
                      swapped
                    )
                  }
                />
              }
              label={<Trans i18nKey="controlStickDriveStyleControl.single_stick">Single stick</Trans>}
            />
          )}
        </Stack>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <PreviewPane
          title={<Trans i18nKey="controlStickDriveStyleControl.left_stick">Left Stick</Trans>}
          src={preview.left}
          alt={t('controlStickDriveStyleControl.left_stick_alt', 'Left stick control preview')}
        />
        <PreviewPane
          title={<Trans i18nKey="controlStickDriveStyleControl.right_stick">Right Stick</Trans>}
          src={preview.right}
          alt={t('controlStickDriveStyleControl.right_stick_alt', 'Right stick control preview')}
        />
      </Stack>
    </Stack>
  );
};

export default ControlStickDriveStyleControl;
